/**
 * useFlowItemMetricsCube — the Flows tab's path × lander × offer metrics for a
 * campaign's flow, from its tracker's flow-item-cube route.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import { flowItemCubePath } from '@/hooks/campaign-tracker';
import type { CampaignProvider } from '@/hooks/voluum/useVoluum';

type Metrics = Record<string, number>;

export type FlowItemMetricsCubeResponse = {
  pathMetricsById: Record<string, Metrics>;
  landerMetricsById: Record<string, Metrics>;
  offerMetricsById: Record<string, Metrics>;
  offerMetricsByLanderId: Record<string, Record<string, Metrics>>;
  offerIdsByLanderId: Record<string, string[]>;
  /** Per-path maps (Binom): pathId → itemId → metrics. Absent for Voluum. */
  landerMetricsByPathId?: Record<string, Record<string, Metrics>>;
  offerMetricsByPathId?: Record<string, Record<string, Metrics>>;
  /** pathId → landerId → offerId → metrics: an offer's stats within a lander. */
  offerMetricsByPathLanderId?: Record<
    string,
    Record<string, Record<string, Metrics>>
  >;
};

const EMPTY_CUBE: FlowItemMetricsCubeResponse = {
  pathMetricsById: {},
  landerMetricsById: {},
  offerMetricsById: {},
  offerMetricsByLanderId: {},
  offerIdsByLanderId: {},
};

export function useFlowItemMetricsCube(params: {
  campaignId: string | undefined;
  flowId: string;
  provider?: CampaignProvider;
  dateRangePreset: string;
  customFrom?: string;
  customTo?: string;
}) {
  const {
    campaignId,
    flowId,
    provider = 'voluum',
    dateRangePreset,
    customFrom,
    customTo,
  } = params;

  const queryParams = useMemo(() => {
    const p: Record<string, string | undefined> = {
      campaignId,
      flowId,
      dateRange: dateRangePreset,
    };
    if (dateRangePreset === 'custom') {
      p.from = customFrom;
      p.to = customTo;
    }
    return p;
  }, [campaignId, flowId, dateRangePreset, customFrom, customTo]);

  return useQuery({
    queryKey: [provider, 'reports', 'flow-item-cube', queryParams],
    queryFn: async () => {
      // Voluum, Binom, or Tres Commas' twin of the Voluum route (same query, same answer).
      const url = flowItemCubePath(provider);
      const response = await api.get<{ data: FlowItemMetricsCubeResponse }>(url, {
        params: queryParams,
      });
      return response.data?.data ?? EMPTY_CUBE;
    },
    enabled: !!campaignId && !!flowId && !!dateRangePreset,
    staleTime: 60000,
  });
}
